<template>
  <div
    class="c-container"
    :class="{ 'c-container-gray' : isContainerGrayPages }"
  >
    <TheHeader
      :master-categories="$store.state.master.masterCategories"
      :jobs="$store.state.master.jobs"
      :blog-categories="$store.state.pages.blogs.master.blogCategoriesList"
      :auth="$store.state.auth"
      :is-logged-in="$store.getters['auth/isLoggedIn']"
      ref="header"
      v-if="!isFromCoconalaApp"
    />
    <nuxt />
    <slot name="footer">
      <ClientOnly>
        <TheFooter
          :is-logged-in="$store.getters['auth/isLoggedIn']"
          ref="footer"
          v-if="!isFromCoconalaApp"
        />
      </ClientOnly>
    </slot>
    <ClientOnly>
      <ServiceStartNotice v-if="$store.getters['auth/isLoggedIn']" />
      <FlashMessage />
      <LoginModal
        :active="isActiveLoginModal"
        @close="closeLoginModal"
      />
      <CakeNotify />
      <DModal
        v-if="isAccountLinkInProgress"
        class="c-accountModal"
        :title="modalTitle"
        :active="isAccountLinkInProgress"
        :is-sp-full-screen="false"
        @close="closeModal"
      >
        <div class="c-accountModalInfo">連携するとアカウントを切り替えながら利用できます。なおアカウントの連携解除はできませんのでご了承ください。</div>
        <div class="c-accountModalCard">
          <span class="c-accountModal_iconWrapper">
            <img
              class="c-accountModal_icon"
              :src="$coconala.userIconURLV2(anotherAccountInfo.resizableProfileIcon)"
              alt=""
            >
          </span>
          <div class="c-accountModal_text">
            <div
              v-if="anotherAccountInfo.enterpriseName"
              class="c-accountModal_enterpriseName"
            >{{ anotherAccountInfo.enterpriseName }}</div>
            <div class="c-accountModal_userName">{{ anotherAccountInfo.userName }}</div>
            <div class="c-accountModal_userEmail">{{ anotherAccountInfo.email }}</div>
          </div>
        </div>
        <div class="c-accountModalButton">
          <DButton
            @click="closeModal"
            type="outlined"
            size="large"
          >
            連携しない
          </DButton>
          <DButton
            @click="doAccountLink"
            size="large"
          >
            連携する
          </DButton>
        </div>
      </DModal>
    </ClientOnly>
  </div>
</template>
<script>
const CONTAINER_GRAY_PAGES = ['mypage-dashboard', 'mypage-dashboard_provider']

import TheHeader from '~/components/organisms/TheHeader'

// vuex
import { mapState, mapActions, mapGetters } from 'vuex'

import { commonMetaData } from '~/store/_helpers/common-helper'
import grpcClient from '@/grpc/grpc-client'
import UserAccessor from '~/api/accessors/user/user-accessor'

const ACCOUNT_LINK_COOKIE_KEY = 'is_account_link_in_progress'
const PRIVATE_CREATION_BY_BUSINESS = 'is_private_creation_by_business'

export default {
  components: {
    TheHeader,
    DButton: () => import('~/components/atoms/DS2/DButton' /* webpackChunkName: "DButton" */),
    DModal: () => import('~/components/molecules/DS2/DModal' /* webpackChunkName: "DModal" */),
    FlashMessage: () =>
      import('~/components/molecules/FlashMessage' /* webpackChunkName: "FlashMessage" */),
    CakeNotify: () =>
      import('~/components/organisms/CakeNotify' /* webpackChunkName: "CakeNotify" */),
    LoginModal: () =>
      import('~/components/organisms/LoginModal' /* webpackChunkName: "LoginModal" */),
    ServiceStartNotice: () =>
      import('~/components/organisms/ServiceStartNotice' /* webpackChunkName: "ServiceStartNotice" */),
    TheFooter: () => import('~/components/organisms/TheFooter' /* webpackChunkName: "TheFooter" */)
  },
  data() {
    return {
      isAccountLinkInProgress: false
    }
  },
  provide() {
    return {
      getFixedHeaderHeight: this.getFixedHeaderHeight
    }
  },
  head() {
    if (!this.$store.state.auth.user.isBusiness) {
      return {}
    }
    return {
      script: [
        {
          src: process.env.config.zendeskWidgetScript,
          id: 'ze-snippet'
        }
      ]
    }
  },
  watch: {
    isLoggedIn: function(v) {
      if (!v) {
        this.$store.dispatch('auth/clearAllModules')
      }
    }
  },
  async mounted() {
    this.updateFixedHeaderHeight(this.getFixedHeaderHeight())
    this.$util.on('resize', window, () => {
      let totalHeight = this.getFixedHeaderHeight()
      if (this.fixedHeaderHeight !== totalHeight) {
        this.updateFixedHeaderHeight(totalHeight)
      }
    })

    // GTM(Google Tag Manager)連携用のdataLayer(変数名は固定)に情報セット。Web接客サービスのタグやCriteo等マーケで使用する
    const userId = this.user.id // Number or null
    const userKey = this.user.userKey // String or ""

    if (!Array.isArray(window.dataLayer)) {
      window.dataLayer = []
    }
    window.dataLayer.push({
      user_id: userId ? String(userId) : '',
      email: userKey
    })

    // 生のemail情報をGTM連携する為に専用APIを追加したので、storeには保存せず上記とはロジックを別とする
    if (userKey) {
      const userApiAccessor = new UserAccessor(this.$store.state)
      userApiAccessor
        .getEmailAddress()
        .then(res => window.dataLayer.push({ raw_email: res?.emailAddress || '' }))
        .catch(() => window.dataLayer.push({ raw_email: '' }))
    } else {
      window.dataLayer.push({ raw_email: '' })
    }

    // Criteo用などのデータ(GTM上に作成された「変数」から参照されている)
    window.COCONALA = {
      IsLogin: userId ? 1 : 0,
      IsProvider: userId && this.user.isProvider ? 1 : 0,
      IsPurchase: userId && this.user.isBuyer ? 1 : 0,
      IsBusiness: userId && this.user.isBusiness ? 1 : 0
    }

    if (this.$route.query.account_switched === '1') {
      // DOMの更新後に実行しないと表示されないため、nextTickを使用
      this.$nextTick(function() {
        this.$notify({
          group: 'flash',
          title: 'アカウントが切り替わりました',
          type: 'success'
        })
      })
    } else if (this.$route.query.account_linked === '1') {
      // DOMの更新後に実行しないと表示されないため、nextTickを使用
      this.$nextTick(() => {
        this.$notify({
          group: 'flash',
          title: 'アカウントが連携されました',
          type: 'success'
        })
      })
    }

    // アカウント紐付け進行中の場合
    if (this.$cookies.get(ACCOUNT_LINK_COOKIE_KEY)) {
      this.$cookies.remove(ACCOUNT_LINK_COOKIE_KEY)
      // アカウントが紐付け可能か確認する
      try {
        if (!this.$store.state.auth.sTokenP || !this.$store.state.auth.sTokenE) {
          throw new Error()
        }
        await this.$store.dispatch('pages/business/accountLink/getLinkStatus', {
          privateSessionKey: this.$store.state.auth.sTokenP,
          businessSessionKey: this.$store.state.auth.sTokenE
        })
      } catch {
        return this.$notify({
          group: 'flash',
          title: 'このアカウントは連携できません',
          type: 'error',
          max: 1
        })
      }
      // accountInfoが両方取得されている場合は既に連携済み、両方取得できない場合はログインしていない状態のためエラーとする
      const existsBusinessAccountInfo = !!Object.keys(this.businessAccountInfo).length
      const existsPrivateAccountInfo = !!Object.keys(this.privateAccountInfo).length
      if (
        (!existsBusinessAccountInfo && !existsPrivateAccountInfo) ||
        (existsBusinessAccountInfo && existsPrivateAccountInfo)
      ) {
        return this.$notify({
          group: 'flash',
          title: 'アカウントの連携に失敗しました',
          type: 'error',
          max: 1
        })
      }
      // 連携したいアカウントの情報を取得する
      try {
        await this.$store.dispatch('pages/business/accountLink/fetchAnotherAccountInfo', {
          anotherToken: existsBusinessAccountInfo
            ? this.$store.state.auth.sTokenP
            : this.$store.state.auth.sTokenE
        })
      } catch {
        return this.$notify({
          group: 'flash',
          title: 'このアカウントは連携できません',
          type: 'error',
          max: 1
        })
      }
      this.isAccountLinkInProgress = true
    }

    const oneTimeNuxtToastMessageKey = process.env.config['one-time-nuxt-toast-message-key']
    const oneTimeNuxtToastMessageValue = this.$cookies.get(oneTimeNuxtToastMessageKey)
    if (oneTimeNuxtToastMessageValue) {
      this.$cookies.remove(oneTimeNuxtToastMessageKey)
      this.$nextTick(() => {
        this.$notify({
          group: 'flash',
          title: oneTimeNuxtToastMessageValue,
          type: 'success'
        })
      })
    }

    if (typeof zE === 'function' && Object.keys(this.businessAccountInfo).length) {
      zE('webWidget', 'prefill', {
        name: {
          value: this.businessAccountInfo.userName,
          readOnly: true // optional
        },
        email: {
          value: this.businessAccountInfo.email,
          readOnly: true // optional
        }
      })

      window.zESettings = {
        webWidget: {
          authenticate: {
            chat: {
              jwtFn: async callback => {
                const requestModule = await import('~/stub/apigateway/business/business_chat_pb')
                const methodModule = await import('~/stub/apigateway/business/business_chat_pb_service')
                const metadata = commonMetaData(this.$store.state)
                const request = new requestModule.GetZendeskChatJwtRequest()
                try {
                  const response = await grpcClient({
                    method: methodModule.BusinessChatService.GetZendeskChatJwt,
                    request,
                    metadata
                  })
                  callback(response.chatJwt)
                } catch (err) {
                  // NOTE: jwt取得のエラーは握りつぶしてSentryに送信
                  this.$sentry.captureException(err)
                }
              }
            }
          }
        }
      }
    }
  },
  methods: {
    ...mapActions('master', ['fetchBasic']),
    ...mapActions('ui', ['updateFixedHeaderHeight', 'closeLoginModal']),
    getFixedHeaderHeight() {
      let $fixableHeaders = document.querySelectorAll('.js-fixableHeader')
      let els = Array.from($fixableHeaders)
      let heights = els.map(el => el.offsetHeight)
      return this.$util.sum(heights)
    },
    closeModal() {
      this.isAccountLinkInProgress = false
      this.$cookies.remove(PRIVATE_CREATION_BY_BUSINESS)
    },
    async doAccountLink() {
      try {
        await this.$store.dispatch('pages/business/accountLink/AddLink', {
          privateSessionKey: this.$store.state.auth.sTokenP,
          businessSessionKey: this.$store.state.auth.sTokenE
        })
      } catch {
        return this.$notify({
          group: 'flash',
          title: 'アカウントの連携に失敗しました',
          type: 'error',
          max: 1
        })
      }
      // アカウントの紐付けが完了した場合、紐付けられたアカウントへ遷移する
      const redirectUrl =
        '/switch_account?switch_for=' +
        this.redirectType +
        '&redirect_to=' +
        this.$route.path +
        '&account_linked=1'
      this.$cookies.remove(PRIVATE_CREATION_BY_BUSINESS)
      location.href = redirectUrl
    }
  },
  computed: {
    ...mapState('ui', ['fixedHeaderHeight', 'isActiveLoginModal']),
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['isFromCoconalaApp', 'isLoggedIn']),
    ...mapState('my/header', ['businessAccountInfo', 'privateAccountInfo']),
    ...mapState('pages/business/accountLink', ['anotherAccountInfo']),
    isContainerGrayPages() {
      return CONTAINER_GRAY_PAGES?.some(name => name === this.$route.name)
    },
    redirectType() {
      // 1: ビジネス→個人への切替、2: 個人→ビジネスへの切替
      // 個人→ビジネスの時、ビジネスアカウントが個人アカウントを作成している場合のみ個人アカウントのままにする
      if (this.user.isBusiness || this.$cookies.get(PRIVATE_CREATION_BY_BUSINESS)) {
        return '1'
      }
      return '2'
    },
    modalTitle() {
      return this.user.isBusiness
        ? '下記の個人・出品アカウントと連携しますか？'
        : '下記のビジネスアカウントと連携しますか？'
    }
  }
}
</script>
<style lang="scss" scoped>
.c-container {
  &-gray {
    background: $color-bg-pale;
  }
}
.c-placeholder {
  transition: height 0.5s;
}
// アカウント紐付けモーダル用定義
/deep/ .d-modal {
  &_title {
    text-align: center;
    white-space: pre-line;
  }
}
.c-accountModalInfo {
  margin-bottom: 24px;
  color: $ds2-color-gray-900;
  letter-spacing: 0.05em;
  font-size: 13px;
  line-height: 19px;
}
.c-accountModalCard {
  box-sizing: border-box;
  margin: 0 auto;
  padding: 16px;
  min-height: 92px;
  width: 363px;
  height: auto;
  box-shadow: 0 4px 8px rgba(2, 16, 66, 0.1);
}
.c-accountModal_text {
  display: inline-block;
  // 横並びの段落ち対策
  width: calc(100% - 82px);
  vertical-align: middle;
}
.c-accountModal_iconWrapper {
  display: inline-block;
  overflow: hidden;
  margin-right: 16px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  vertical-align: middle;
}
.c-accountModal_icon {
  display: block;
  width: 100%;
  height: 100%;
}
.c-accountModal_enterpriseName {
  color: $ds2-color-gray-700;
  letter-spacing: 0.05em;
  font-weight: bold;
  font-size: 11px;
  line-height: 16px;
}
.c-accountModal_userName {
  color: $ds2-color-gray-900;
  letter-spacing: 0.05em;
  font-size: 14px;
  line-height: 21px;
}
.c-accountModal_userEmail {
  color: $ds2-color-gray-400;
  letter-spacing: 0.05em;
  font-size: 12px;
  line-height: 18px;
}
.c-accountModalButton {
  margin-top: 32px;
  text-align: center;
  /deep/ .d-button {
    width: 120px;
    font-weight: bold;
    &:first-child {
      margin-right: 14px;
    }
  }
}

@media (max-width: $breakpoint-l - 1px) {
  // アカウント紐付けモーダル用定義
  .c-accountModal {
    /deep/ .d-modal {
      &_title {
        box-sizing: content-box;
        margin: 0 auto;
        padding-bottom: 8px;
        width: 13em;
        font-size: 16px;
        line-height: 22px;
      }
    }
  }
  .c-accountModalInfo {
    letter-spacing: 0.02em;
    font-size: 14px;
    line-height: 20px;
  }
  .c-accountModalCard {
    padding: 12px;
    width: 100%;
    height: auto;
  }
  .c-accountModal_iconWrapper {
    margin-right: 8px;
    width: 40px;
    height: 40px;
  }
  .c-accountModal_text {
    width: calc(100% - 54px);
  }
  .c-accountModalButton {
    /deep/ .d-button {
      width: calc(50% - 4px);
      &:first-child {
        margin-right: 0.2rem;
      }
    }
  }
}
</style>

<style lang="scss">
@media (max-width: $ds2-breakpoint-s) {
  #launcher {
    display: none;
  }
}
</style>
